import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { StageBadge, TipoClienteIcon, LoadingState, EmptyState, FilterChip } from '../../components/comercial/shared';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Users, Search, UserPlus, Phone, MapPin } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_FILTERS = [
  { id: 'todos', label: 'Todos' },
  { id: 'contacto_nuevo', label: 'Contacto Nuevo' },
  { id: 'interesado', label: 'Interesado' },
  { id: 'pendiente_instalacion_prueba', label: 'Pdte. Prueba' },
  { id: 'prueba_activa', label: 'Prueba Activa' },
  { id: 'pendiente_contrato', label: 'Pdte. Contrato' },
  { id: 'cliente', label: 'Cliente' },
  { id: 'perdido', label: 'Perdido' },
];

export default function ContactosPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trials, setTrials] = useState([]);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('todos');
  const [tipoFilter, setTipoFilter] = useState('todos');

  const fetchTrials = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/comercial/trials`, { withCredentials: true });
      setTrials(response.data || []);
    } catch (err) {
      console.error('Error fetching trials:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrials(); }, [fetchTrials]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filtered = trials.filter((t) => {
    if (stageFilter !== 'todos' && t.crm_stage !== stageFilter) return false;
    if (tipoFilter !== 'todos' && t.tipo_cliente !== tipoFilter) return false;
    if (search.length >= 2) {
      const q = search.toLowerCase();
      const fields = [t.nombre_contacto, t.empresa, t.telefono, t.email, t.ciudad, t.client_code];
      if (!fields.some((f) => (f || '').toLowerCase().includes(q))) return false;
    }
    return true;
  });

  return (
    <ComercialLayout
      title="Contactos"
      subtitle={`${trials.length} contactos registrados`}
      onLogout={handleLogout}
      headerActions={
        <Button
          onClick={() => navigate('/comercial/nuevo-contacto')}
          className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
          data-testid="contactos-nuevo-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Nuevo Contacto
        </Button>
      }
    >
      <div className="space-y-4" data-testid="contactos-page">
        {/* Filtros */}
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, teléfono, código..."
              className="pl-9 w-full lg:w-72 bg-white/[0.03] border-white/10 text-white placeholder:text-zinc-600"
              data-testid="contactos-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {STAGE_FILTERS.map((f) => (
              <FilterChip
                key={f.id}
                active={stageFilter === f.id}
                onClick={() => setStageFilter(f.id)}
                testId={`contactos-filter-${f.id}`}
              >
                {f.label}
              </FilterChip>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          {[{ id: 'todos', label: 'Hogar + Empresa' }, { id: 'hogar', label: 'Hogar' }, { id: 'empresa', label: 'Empresa' }].map((f) => (
            <FilterChip
              key={f.id}
              active={tipoFilter === f.id}
              onClick={() => setTipoFilter(f.id)}
              testId={`contactos-tipo-${f.id}`}
            >
              {f.label}
            </FilterChip>
          ))}
        </div>

        {/* Listado */}
        {loading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Sin contactos"
            description="No hay contactos que coincidan con los filtros seleccionados."
          />
        ) : (
          <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Cliente</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium hidden md:table-cell">Contacto</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium hidden lg:table-cell">Origen</th>
                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => navigate(`/comercial/cliente/${t.id}`)}
                      className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors"
                      data-testid={`contacto-row-${t.client_code}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <TipoClienteIcon tipo={t.tipo_cliente} />
                          <div>
                            <p className="text-sm text-white">{t.tipo_cliente === 'empresa' ? t.empresa : t.nombre_contacto}</p>
                            <span className="text-xs font-mono text-zinc-500">{t.client_code}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="space-y-0.5">
                          {t.telefono && (
                            <p className="text-xs text-zinc-400 flex items-center gap-1">
                              <Phone className="w-3 h-3" />{t.telefono}
                            </p>
                          )}
                          {t.ciudad && (
                            <p className="text-xs text-zinc-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />{t.ciudad}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-xs text-zinc-400 capitalize">{(t.origen_cliente || '').replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <StageBadge stage={t.crm_stage} label={t.stage_label} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </ComercialLayout>
  );
}
